import { Group } from '@mantine/core';
import { Button, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import type { QuestionStore } from '../store';
import { QuestionReplyBody } from './QuestionReplyBody';
import { QuestionReplyInput } from './QuestionReplyInput';

/**
 * Converts Slack-style links (<url|text> or <url>) to markdown format ([text](url))
 */
function convertSlackLinks(text: string): string {
	return text.replace(
		/<([^@>][^>]*?)(?:\|([^>]+))?>/g,
		(_, url, linkText) => `[${linkText || url}](${url})`
	);
}

export const UserReply = observer(
	({ replyId, store }: { replyId: string; store: QuestionStore }) => {
		const reply = store.replies.get(replyId);

		if (!reply) {
			return null;
		}

		const handleChange = useCallback(
			(value: string | undefined) => {
				reply.setEditingDefinition(value ?? '');
			},
			[reply]
		);

		const isFocused = store.focusedReplyId === replyId;
		const normalizedDefinition = (reply.reply.definition ?? '')
			.trim()
			.toLowerCase();
		const isDeleted = normalizedDefinition === 'deleted';

		// Convert Slack links when displaying the reply content
		const convertedInitialValue = useMemo(() => {
			const rawValue = reply.editingDefinition ?? reply.reply.definition;
			return rawValue ? convertSlackLinks(rawValue) : rawValue;
		}, [reply.editingDefinition, reply.reply.definition]);

		return (
			<QuestionReplyBody
				replyId={replyId}
				store={store}
				showToolbar={reply.editing}
				isFocused={isFocused}
			>
				{isDeleted && !reply.editing ? (
					<Text size="sm" color="text/secondary/default">
						deleted
					</Text>
				) : (
					<QuestionReplyInput
						key={reply.editingEditorKey}
						dataTestId={`reply-editor-${reply.reply.id}`}
						emptyLinePlaceholder={'Write something or press "/" for actions...'}
						emptyDocPlaceholder="Edit reply..."
						readOnly={!reply.editing}
						onSubmit={reply.saveEditing}
						onChange={handleChange}
						initialValue={convertedInitialValue}
					/>
				)}
				{reply.editing && (
					<Group spacing="3xs" style={{ alignSelf: 'flex-end' }}>
						<Button variant="tertiary" size="sm" onClick={reply.cancelEditing}>
							Cancel
						</Button>
						<Button
							variant="primary"
							size="sm"
							onClick={reply.saveEditing}
							disabled={
								!reply.editingDefinition?.length || reply.editingLoading
							}
							loading={reply.editingLoading}
						>
							Save
						</Button>
					</Group>
				)}
			</QuestionReplyBody>
		);
	}
);
